/* eslint-disable camelcase */
/* eslint-disable dot-notation */
import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Modal from 'react-bootstrap/Modal';
import { useTranslation } from 'react-i18next';

import { previousModalPage } from 'actions/modalPage';
import { SaveButton } from 'components/UIElements/UIButton';
import {
  getSettings,
  getIsCustomPaymentEnabled,
} from 'reducers/configs/settings';
import Loader from 'components/Loader';
import { addWarning, addError } from 'actions/Error';
import CloseButton from 'components/CustomButtons/CloseButton';
import { saveSetting } from 'actions/configs';
import {
  Page,
  Section,
  collapseAllSections
} from "containers/Forms/Settings/components/Inputs";
import { Ctx, Check } from 'containers/Forms/Settings/components/CtxInputs';
import { modalPages } from 'constants/modalPage';

import {
  SalesTenders,
  ReturnTenders,
  ReturnWithoutReceipt,
} from './components';
import styles from './Payment.module.scss';
import * as MUI from "@material-ui/core";

const Payment = () => {
  const [saving, setSaving] = useState(false);
  const dispatch = useDispatch();
  const isCustomPaymentEnabled = useSelector(getIsCustomPaymentEnabled);

  const { t } = useTranslation('settingsPayment');
  const onClose = () => dispatch(previousModalPage());

  const [changed, setChanged] = useState({});
  const current = useSelector(getSettings);

  const onSave = async () => {
    setSaving(true);
    const {
      pos_allow_sale_cash,
      pos_allow_sale_card,
      pos_allow_sale_giftcard_serial,
      pos_allow_sale_giftcard_regular,
      pos_allow_sale_storecredit,
      pos_allow_sale_check,
      pos_allow_sale_tip,
    } = values;
    const tenders = [
      pos_allow_sale_cash,
      pos_allow_sale_card,
      pos_allow_sale_giftcard_serial,
      pos_allow_sale_giftcard_regular,
      pos_allow_sale_storecredit,
      pos_allow_sale_check,
      pos_allow_sale_tip,
    ];
    const saleDisabled = !tenders.reduce(
      (result, tender) => result || !!tender,
      false,
    );
    if (saleDisabled) {
      dispatch(
        addWarning('All sale payments are disabled !', { selfDismiss: true }),
      );
    }
    const {
      pos_allow_return_receipt_cash,
      pos_allow_return_receipt_card,
      pos_allow_return_receipt_giftcard_serial,
      pos_allow_return_receipt_giftcard_regular,
      pos_allow_return_receipt_storecredit,
      pos_allow_return_receipt_check,
      pos_allow_return_receipt_tip,
    } = values;
    const returnReceiptTenders = [
      pos_allow_return_receipt_cash,
      pos_allow_return_receipt_card,
      pos_allow_return_receipt_giftcard_serial,
      pos_allow_return_receipt_giftcard_regular,
      pos_allow_return_receipt_storecredit,
      pos_allow_return_receipt_check,
      pos_allow_return_receipt_tip,
    ];
    const returnReceiptDisabled = !returnReceiptTenders.reduce(
      (result, tender) => result || !!tender,
      false,
    );
    if (returnReceiptDisabled) {
      dispatch(
        addWarning('All return with receipt payments are disabled !', {
          selfDismiss: true,
        }),
      );
    }

    try {
      Promise.all(
        Object.entries(changed).map(([key, value]) => {
          let parameterValue;

          if (key.includes('limit'))
            parameterValue =
              !Number.isNaN(Number(value)) && Number(value) > 0 ? value : '';
          else parameterValue = value ? 1 : 0;

          return dispatch(
            saveSetting({
              parameterName: key,
              parameterValue,
            }),
          );
        }),
      ).then(onClose);
    } catch (e) {
      console.error('Failed to revert settings in aborted form', changed, e);
      dispatch(addError(e.message, { selfDismiss: true }));
    } finally {
      setSaving(false);
    }
  };

  // Menu that allows you to setup custom payments
  const customPayments = isCustomPaymentEnabled && (
    <Page title={t('customPayments.title')} value={modalPages.CustomPayments} />
  );

  const onChange = useCallback(
    (key, value) => {
      if (
        !key.includes('limit') &&
        value === 0 &&
        !key.includes('pos_allow_return_receipt_original_tender_only')
      ) {
        setChanged(state => ({ ...state, [`${key}_limit`]: '' }));
      }
      if (current[key] === value) {
        setChanged(state => {
          const newState = { ...state };
          delete newState[key];
          return newState;
        });
      } else {
        setChanged(state => ({ ...state, [key]: value }));
      }
      return undefined;
    },
    [current],
  );

  const values = {
    ...current,
    ...changed,
  };

  return (
    <div className={styles['erply-settings__payment']} style={{ overflowY: 'auto' }}>
      <Loader
        show={saving}
        loadingText="Saving..."
        style={{ display: 'block', height: '100%', maxHeight: '100%' }}
      >
        <Modal.Header>
          <span>{t('title')}</span>
          <span style={{ flexGrow: '1' }} />
          <SaveButton variant="POS" action={onSave} />

          <CloseButton action={onClose} />
        </Modal.Header>
        <Modal.Body>
          <Ctx.Provider value={{ values, onChange }} values={values}>
            <MUI.Grid container>
              <MUI.Grid item xs/>
              <MUI.Button variant="text" onClick={collapseAllSections}>
                {t('settings:buttons.collapseAllSections')}
              </MUI.Button>
            </MUI.Grid>
            <Section title={t('general.title')}>
              <Page
                title={t('general.setupGateways')}
                value={modalPages.PaymentGateways}
              />
              {/* Temporarily disabled as per https://erply.atlassian.net/browse/PBIB-1750?focusedCommentId=187006 */}
              {/* <Check */}
              {/*   name="use_cayan_capture" */}
              {/*   title={t('general.capturePayments')} */}
              {/* /> */}
              <Check
                // Is used to enable/disable Cash Out/Withdrawal functionality (previously named Cash Back)
                name="disableCashBack"
                title={t('general.disableCashBack')}
              />
            </Section>
            <Section title={t('customPayments.title')}>
              <Check
                name="touchpos_enable_custom_payments"
                title={t('customPayments.enable')}
              />
              {/* Menu that allows you to setup custom payments */}
              {customPayments}
            </Section>

            <Section title={t('sale.title')}>
              <SalesTenders prefix="sale" />
            </Section>

            <Section title={t('returnWithReceipt.title')}>
              <Check
                title={t('returnWithReceipt.originalTenderOnly')}
                name="pos_allow_return_receipt_original_tender_only"
              />
              <ReturnTenders prefix="returnWithReceipt" />
            </Section>

            <Section title={t('returnWithoutReceipt.title')}>
              <ReturnWithoutReceipt prefix="returnWithoutReceipt" />
            </Section>
          </Ctx.Provider>
        </Modal.Body>
      </Loader>
    </div>
  );
};

export default Payment;
